import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { AlertContainer } from '@/common/components/Alert';
import Grid from '@/common/components/Grid';
import RecoveryModal from '@/common/components/RecoveryModal';
import { ToastContainer } from '@/common/components/Toast';
import { UpgradeModal } from '@/common/components/UpgradeModal';
import WidgetMenu from '@/common/components/WidgetMenu';
import { useBoardStore } from '@/common/stores/useBoardStore';
import { useSubscriptionStore } from '@/common/stores/useSubscriptionStore';
import { useToastStore } from '@/common/stores/useToastStore';
import { useWidgetStore, Widget } from '@/common/stores/useWidgetStore';
import { AVAILABLE_WIDGETS } from '@/common/utils/constants';

const Dashboard = () => {
  const { t } = useTranslation();
  const {
    widgets,
    layouts,
    addWidget,
    fetchWidgets,
    removeWidget,
    getDiscardedWidgets,
    recoverWidget,
  } = useWidgetStore();
  const { fetchSubscription, isWidgetLocked } = useSubscriptionStore();
  const { showToast } = useToastStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isRecoveryModalOpen, setIsRecoveryModalOpen] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [selectedWidgetId, setSelectedWidgetId] = useState<string | null>(null);
  const [discardedWidgets, setDiscardedWidgets] = useState<Widget[]>([]);

  const currentBoard = useBoardStore((state) => state.currentBoard);

  useEffect(() => {
    if (currentBoard) {
      fetchWidgets();
    }
  }, [currentBoard, fetchWidgets]);

  useEffect(() => {
    const subscriptionStatus = searchParams.get('subscription');
    if (subscriptionStatus === 'success') {
      fetchSubscription();
      showToast({
        type: 'success',
        title: t('dashboard.welcomePremium'),
        message: t('dashboard.welcomePremiumMessage'),
        duration: 5,
      });
      setSearchParams({}, { replace: true });
    } else if (subscriptionStatus === 'canceled') {
      fetchSubscription();
      showToast({
        type: 'info',
        title: t('dashboard.subscriptionCanceled'),
        message: t('dashboard.subscriptionCanceledMessage'),
        duration: 5,
      });
      setSearchParams({}, { replace: true });
    }
  }, [fetchSubscription, searchParams, setSearchParams, showToast, t]);

  const handleAddWidget = async (widgetId: string) => {
    const discarded = await getDiscardedWidgets(widgetId);
    const widgetDetails = AVAILABLE_WIDGETS.find((w) => w.id === widgetId);

    if (discarded.length > 0) {
      if (widgetDetails?.recoverable) {
        // If widget is recoverable, show recovery modal
        setDiscardedWidgets(discarded);
        setSelectedWidgetId(widgetId);
        setIsRecoveryModalOpen(true);
      } else {
        // If widget is not recoverable, automatically recover the first discarded instance
        await recoverWidget(discarded[0]);
      }
    } else {
      await addWidget(widgetId);
    }
  };

  const handleRecoverWidget = async (widget: Widget) => {
    await recoverWidget(widget);
    setIsRecoveryModalOpen(false);
    setSelectedWidgetId(null);
  };

  const handleCreateNew = async () => {
    if (selectedWidgetId) {
      await addWidget(selectedWidgetId);
    }
    setIsRecoveryModalOpen(false);
    setSelectedWidgetId(null);
  };

  const handleRemoveWidget = async (widgetId: string) => {
    await removeWidget(widgetId);
  };

  const gridItems = widgets
    .map((widget) => {
      const widgetDetails = AVAILABLE_WIDGETS.find((w) => w.id === widget.widget_id);
      if (!widgetDetails) {
        return null;
      }
      return {
        ...widgetDetails,
        metadata: widget.metadata,
        id: widget.id,
        widget_id: widget.widget_id,
        isLocked: isWidgetLocked(widget.id, widget.widget_id, widgets, layouts),
      };
    })
    .filter((item): item is NonNullable<typeof item> => item !== null);

  return (
    <>
      <AlertContainer />
      <ToastContainer />
      <div
        className="min-h-screen bg-gray-50 p-6"
        style={{
          backgroundImage: 'radial-gradient(circle, #d1d5db 0.75px, transparent 0.75px)',
          backgroundSize: '32px 32px',
        }}
      >
        <div className="flex justify-between items-center mb-4">
          <WidgetMenu
            activeWidgets={widgets.map((w) => w.widget_id)}
            onAddWidget={handleAddWidget}
          />
        </div>
        <Grid
          items={gridItems}
          onLockedWidgetClick={() => setShowUpgradeModal(true)}
          onRemoveWidget={handleRemoveWidget}
        />
      </div>
      <RecoveryModal
        discardedWidgets={discardedWidgets}
        isOpen={isRecoveryModalOpen}
        onClose={() => setIsRecoveryModalOpen(false)}
        onCreateNew={handleCreateNew}
        onRecover={handleRecoverWidget}
      />
      <UpgradeModal isOpen={showUpgradeModal} onClose={() => setShowUpgradeModal(false)} />
    </>
  );
};

export default Dashboard;
